import React from 'react'
import styles from './index.less'
import isNumber from 'lodash/isNumber'
import isNil from 'lodash/isNil'
import type { TooltipProps } from 'antd'
import { Divider, Space, Tooltip } from 'antd'
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined'
import classNames from 'classnames'

interface Solt {
    /**
     * 插槽内容
     */
    content: React.ReactNode
    /**
     * 插槽内容渲染函数
     */
    renderContent?: (node: React.ReactNode) => React.ReactNode
    // 计数
    count?: React.ReactNode | (() => React.ReactNode) // 计数
    /**
     * 提示
     */
    tip?: React.ReactNode // 提示
    /**
     * 提示的toolTip的props
     */
    tooltipProps?: Partial<TooltipProps>
    /**
     * 提示图标
     */
    tipIcon?: React.ReactNode
    /**
     * 隐藏插槽后的横线
     */
    hideDivider?: boolean
    /**
     * 插槽的style
     */
    style?: React.CSSProperties
    /**
     * 自定义classname
     */
    className?: string
    /**
     * 自定义渲染tip
     */
    renderTip?: () => React.ReactNode
    /**
     * 插槽的itemTootips
     */
    itemTootips?: string
    /**
     * 自定义icon
     */
    customIcon?: React.ReactNode
    /**
     * 是否隐藏
     */
    hidden?: boolean
    /**
     * 点击事件
     */
    onClick?: (e) => void
}

export const defaultSelectIcon =
    'https://assets.sursung.com/activity/20231007/10448F887EDA41CF819308C022EFBAD81696660239830.svg'

/**
 *
 * @param content 内容
 * @param count 计数
 * @param tip 提示
 * @param tipIcon 提示的icon
 * @param hideDivider 隐藏当前插槽的后置横线
 * @param className 自定义classname
 * @param style 自定义容器style
 */
const Solt = ({
    content,
    count,
    tip,
    tipIcon,
    itemTootips,
    hideDivider,
    className,
    style,
    tooltipProps,
    renderTip,
    customIcon,
    onClick
}: Solt) => {
    const renderCurTip = () => {
        if (typeof renderTip === 'function') {
            return renderTip()
        }
        if (tip) {
            return (
                <Tooltip
                    overlayInnerStyle={{ fontSize: '12px' }}
                    arrowPointAtCenter
                    placement="top"
                    title={tip}
                    className={styles.tipIcon}
                    {...(tooltipProps || {})}
                >
                    {tipIcon ? tipIcon : <QuestionCircleOutlined />}
                </Tooltip>
            )
        }
        return null
    }

    const renderItemBox = (node: React.ReactNode, tootips?: string) => {
        if (tootips) {
            return (
                <Tooltip
                    overlay={null}
                    getPopupContainer={triggerNode => triggerNode?.parentElement?.parentElement}
                    placement="topLeft"
                    overlayStyle={{ fontSize: '12px' }}
                    title={tootips}
                    color="rgba(0,0,0,0.7)"
                >
                    {node}
                </Tooltip>
            )
        }
        return node
    }

    const renderCustomIcon = () => {
        if (customIcon) {
            return customIcon
        }
    }

    const renderCount = () => {
        if (typeof count === 'function') {
            return count()
        }
        if (isNumber(count)) {
            return <div className={styles.count}>{count}</div>
        }

        if (!isNil(count)) {
            return count
        }
        return null
    }
    return (
        <div className={className} style={style} onClick={onClick}>
            {renderItemBox(
                <Space size={4}>
                    {typeof content === 'string' ? <img className={styles.image} src={content} /> : content}
                    {renderCount()}
                    {renderCurTip()}
                    {renderCustomIcon?.()}
                </Space>,
                itemTootips
            )}

            {!hideDivider && <Divider className={styles.divider} type="vertical" />}
        </div>
    )
}

interface SelectCheckBoxWrapperProps {
    /**
     * 是否选中
     */
    isSelected?: boolean
    /**
     * 自定义选中的icon
     */
    renderSelectIcon?: React.ReactNode | ((isSelected?: boolean) => React.ReactNode)
    /**
     * 点击事件
     */
    onClick?: () => void
    /**
     * 点击第一个插槽的事件
     */
    onClickFirstSolt?: () => void
    /**
     * 插槽
     */
    solts: Solt[]
    /**
     * 后置的按钮
     */
    addonAfter?: React.ReactNode
    /**
     * 是否显示右上角的new图标
     */
    showNewIcon?: boolean
    /**
     * 自定义右上角的new图标的url
     */
    newIconUrl?: string
    /**
     * 自定义整个组件外层容器的样式
     */
    className?: string
    /**
     * 插槽的tootips
     */
    tootips?: string
}

// 通用的ui组件，提供的功能
// 1.选中的效果以及整体的ui设计
// 2.提供3个内容插槽以及后置按钮（插槽的定义为横线来区分，分成前，中，后）
// 3.数字显示以及提示的图标
// 4.右上角new文字
/**
 *
 * @param isSelected 是否选中
 * @param renderSelectIcon 自定义选中的icon
 * @param onClick 点击事件
 * @param solts 主要内容插槽
 * @param addonAfter 后置的按钮
 * @param showNewIcon 是否显示右上角默认的new的icon
 * @param className className
 */
const SceneFilteringBoxWrapper = ({
    isSelected,
    renderSelectIcon = defaultSelectIcon,
    //  这个onClick事件不建议使用（待废弃），更建议使用onClickFirstSolt，因为onClick会响应相应子元素的冒泡事件，
    // 带来没有必要的困恼，而onClickFirstSolt响应的是第一条插槽内容的点击事件
    onClick = () => {},
    onClickFirstSolt = () => {},
    solts = [],
    addonAfter,
    showNewIcon,
    newIconUrl,
    newIconStyle,
    className,
    tootips,
    ...divProp
}: SelectCheckBoxWrapperProps & JSX.IntrinsicElements['div']) => {
    const curSelectIcon = () => {
        if (typeof renderSelectIcon === 'function') {
            return renderSelectIcon?.(isSelected)
        }
        if (typeof renderSelectIcon === 'string') {
            return <img className={styles.image} src={renderSelectIcon} onClick={onClickFirstSolt} />
        }

        return renderSelectIcon
    }
    const curNotSelect = () => {
        return <div className={styles.notSelect} onClick={onClickFirstSolt} />
    }
    const curSelectAfter = () => {
        return <div className={styles.selectAfter} onClick={onClickFirstSolt} />
    }

    const renderAfterSelect = () => {
        if (solts?.length === 1) {
            return curSelectAfter()
        }
    }

    const renderContent = (solts: Solt[]) => {
        return (
            <div className={styles.soltsContent}>
                {solts?.map((item, index) => {
                    const getHideDivider = () => {
                        if (item?.hideDivider) {
                            return item.hideDivider
                        }
                        if (solts.length - 1 === index) {
                            return true
                        }
                        return false
                    }

                    if (typeof item.renderContent === 'function') {
                        return item.renderContent(
                            <div key={index}>
                                <Solt {...item} hideDivider={getHideDivider()} />
                            </div>
                        )
                    }
                    return (
                        <div key={index}>
                            <Solt {...item} hideDivider={getHideDivider()} />
                        </div>
                    )
                })}
            </div>
        )
    }

    const renderAddonAfter = () => {
        if (!addonAfter) {
            return null
        }
        return (
            <div
                className={classNames(styles?.addonAfterItem, {
                    [styles.addonAfterItemSelected]: isSelected
                })}
            >
                {addonAfter}
            </div>
        )
    }

    const renderNewIcon = () => {
        if (!showNewIcon) {
            return null
        }
        return (
            <div>
                <img
                    className={styles.newIcon}
                    style={newIconStyle}
                    src={
                        newIconUrl ??
                        'https://assets.sursung.com/activity/2024_imgs/dis/front/238BEB8DA3544097BDDEC3AADC9576E51663052532847.jpg'
                    }
                />
            </div>
        )
    }

    const renderBox = (node: React.ReactNode, tootips?: string) => {
        if (tootips) {
            return (
                <Tooltip
                    overlay={null}
                    getPopupContainer={triggerNode => triggerNode?.parentElement?.parentElement}
                    placement="topLeft"
                    overlayStyle={{ fontSize: '12px' }}
                    title={tootips}
                    color="rgba(0,0,0,0.7)"
                >
                    {node}
                </Tooltip>
            )
        }
        return node
    }
    return (
        <div
            className={classNames(
                styles.sceneFilterbox,
                {
                    [styles.boxSelected]: isSelected
                },
                className
            )}
            onClick={onClick}
            {...divProp}
        >
            {renderBox(
                <div
                    className={classNames(styles.contentBox, {
                        [styles.boxSelected]: isSelected
                    })}
                >
                    {isSelected ? curSelectIcon() : curNotSelect()}
                    {renderContent(solts)}
                    {renderAfterSelect()}
                </div>,
                tootips
            )}
            {renderAddonAfter()}
            {showNewIcon && renderNewIcon()}
        </div>
    )
}

export default SceneFilteringBoxWrapper
